"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useCallback, useEffect } from "react";
import { Map, useMap } from "@vis.gl/react-google-maps";
import AdvancedMarker from "./AdvancedMarker";

/**
 * Pans the map when center changes (e.g. after search selection).
 * Must be rendered inside Map.
 */
function MapPanController(t0) {
  const $ = _c(7);
  const {
    center
  } = t0;
  const map = useMap();
  let t1;
  if ($[0] !== center || $[1] !== map) {
    t1 = () => {
      if (!map || !center?.lat || !center?.lng) {
        return;
      }
      map.panTo({
        lat: center.lat,
        lng: center.lng
      });
    };
    $[0] = center;
    $[1] = map;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = center?.lat;
  const t3 = center?.lng;
  let t4;
  if ($[3] !== map || $[4] !== t2 || $[5] !== t3) {
    t4 = [map, t2, t3];
    $[3] = map;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  useEffect(t1, t4);
  return null;
}

/**
 * Map Component for location selection
 * Handles map clicks and marker dragging
 *
 * @param {Object} props
 * @param {Object} props.center - Initial center position { lat: number, lng: number }
 * @param {Function} props.onPositionChange - Callback when position changes
 */
export default function MapComponent(t0) {
  const $ = _c(18);
  const {
    center,
    onPositionChange
  } = t0;
  const [markerPosition, setMarkerPosition] = useState(center);
  let t1;
  let t2;
  if ($[0] !== center) {
    t1 = () => {
      if (center) {
        setMarkerPosition(center);
      }
    };
    t2 = [center];
    $[0] = center;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== onPositionChange) {
    t3 = event => {
      const lat = event.detail?.latLng?.lat || event.latLng?.lat();
      const lng = event.detail?.latLng?.lng || event.latLng?.lng();
      if (lat && lng) {
        const newPosition = {
          lat,
          lng
        };
        setMarkerPosition(newPosition);
        onPositionChange(newPosition);
      }
    };
    $[3] = onPositionChange;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const handleMapClick = t3;
  let t4;
  if ($[5] !== onPositionChange) {
    t4 = position => {
      setMarkerPosition(position);
      onPositionChange(position);
    };
    $[5] = onPositionChange;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const handleMarkerDrag = t4;
  if (!center) {
    return null;
  }
  const mapId = process.env.NEXT_PUBLIC_GOOGLE_MAPS_MAP_ID;
  if (!mapId) {
    console.error("NEXT_PUBLIC_GOOGLE_MAPS_MAP_ID is not configured. AdvancedMarker requires a Map ID.");
    return null;
  }
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      width: "100%",
      height: "100%"
    };
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== center) {
    t6 = <MapPanController center={center} />;
    $[8] = center;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== handleMarkerDrag || $[11] !== markerPosition) {
    t7 = <AdvancedMarker position={markerPosition} onDragEnd={handleMarkerDrag} />;
    $[10] = handleMarkerDrag;
    $[11] = markerPosition;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== center || $[14] !== handleMapClick || $[15] !== t6 || $[16] !== t7) {
    t8 = <Map defaultCenter={center} defaultZoom={15} gestureHandling="greedy" disableDefaultUI={false} mapTypeControl={false} streetViewControl={false} fullscreenControl={false} onClick={handleMapClick} mapId={mapId} style={t5}>{t6}{t7}</Map>;
    $[13] = center;
    $[14] = handleMapClick;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  return t8;
}